import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  /** صور إضافية للمشاركة (og:image متعدد) */
  ogImages?: string[];
  ogType?: string;
  /** Edge Function URL أو أي رابط بديل يُستخدم في og:url (البوتات تتبعه للحصول على preview card كاملة) */
  ogUrl?: string;
  jsonLd?: object | object[];
  noIndex?: boolean;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeJsonLd() {
  document.querySelectorAll('script[type="application/ld+json"][data-seo="dynamic"]').forEach((el) => el.remove());
}

function injectJsonLd(data: object | object[]) {
  removeJsonLd();
  const items = Array.isArray(data) ? data : [data];
  items.forEach((item) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "dynamic");
    script.textContent = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

/** حذف جميع og:image tags الديناميكية */
function removeOgImages() {
  document.querySelectorAll('meta[property="og:image"][data-dynamic], meta[property="og:image:secure_url"][data-dynamic], meta[property="og:image:width"][data-dynamic], meta[property="og:image:height"][data-dynamic], meta[property="og:image:type"][data-dynamic], meta[property="og:image:alt"][data-dynamic]').forEach((el) => el.remove());
}

/** حقن og:image tags لصورة واحدة */
function injectOgImage(url: string, alt: string, isPrimary: boolean) {
  const props: [string, string][] = [
    ["og:image", url],
    ["og:image:secure_url", url],
    ["og:image:width", "1200"],
    ["og:image:height", "630"],
    ["og:image:type", url.endsWith(".png") ? "image/png" : url.endsWith(".webp") ? "image/webp" : "image/jpeg"],
    ["og:image:alt", alt],
  ];
  props.forEach(([prop, content]) => {
    const el = document.createElement("meta");
    el.setAttribute("property", prop);
    el.setAttribute("content", content);
    el.setAttribute("data-dynamic", "og-image");
    if (isPrimary) el.setAttribute("data-primary", "true");
    document.head.appendChild(el);
  });
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogImages,
  ogType = "website",
  ogUrl,
  jsonLd,
  noIndex = false,
}: SEOProps) {
  useEffect(() => {
    const prevTitle = document.title;

    document.title = title;

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta("robots", noIndex ? "noindex, nofollow" : "index, follow");

    const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    setLink("canonical", canonicalUrl);

    // Last-modified
    setMeta("last-modified", new Date().toISOString().split("T")[0]);

    // OG tags
    setMeta("og:title", ogTitle || title, "property");
    setMeta("og:description", ogDescription || description, "property");
    setMeta("og:type", ogType, "property");
    // og:url يمكن أن يكون Edge Function URL (للبوتات) أو الرابط الحقيقي
    const ogUrlValue = ogUrl || canonicalUrl;
    setMeta("og:url", ogUrlValue, "property");
    setMeta("og:locale", "ar_SA", "property");
    setMeta("og:site_name", "السياحية", "property");

    // جمع كل الصور بدون تكرار
    const allImages: string[] = [];
    if (ogImage && !ogImage.startsWith("data:")) allImages.push(ogImage);
    if (ogImages) {
      ogImages.forEach((img) => {
        if (img && !img.startsWith("data:") && !allImages.includes(img)) {
          allImages.push(img);
        }
      });
    }

    // حذف القديمة وحقن الجديدة
    removeOgImages();
    const altText = ogTitle || title;
    allImages.forEach((img, i) => {
      injectOgImage(img, altText, i === 0);
    });

    // Twitter Card — تستخدم أول صورة دائماً
    const primaryImage = allImages[0] || null;
    setMeta("twitter:card", primaryImage ? "summary_large_image" : "summary");
    setMeta("twitter:site", "@alsiyahiah");
    setMeta("twitter:title", ogTitle || title);
    setMeta("twitter:description", ogDescription || description);
    if (primaryImage) {
      setMeta("twitter:image", primaryImage);
      setMeta("twitter:image:alt", altText);
    }

    // JSON-LD
    if (jsonLd) {
      injectJsonLd(jsonLd);
    } else {
      removeJsonLd();
    }

    return () => {
      document.title = prevTitle;
      removeJsonLd();
      removeOgImages();
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogImages, ogType, ogUrl, jsonLd, noIndex]);
}

export { SITE_URL };
